import { useEffect, useState } from 'react';
import {
  Box, Flex, Text, Badge, Button, Input, Select, Switch, useToast, useColorMode,
  SimpleGrid, Divider, useColorModeValue,
} from '@chakra-ui/react';
import Card from 'components/card/Card';
import { api } from 'lib/api';
import { useApi } from 'lib/useApi';
import { useAuth } from 'lib/auth';
import { fmtDateTime, RecordStatus, RecordTier } from 'views/vdh/Users';

const TIERS = [
  { key: 'free', name: 'Free', price: '0đ', feats: ['1 máy', 'Trắc nghiệm cơ bản'] },
  { key: 'standard', name: 'Standard', price: '79k/tháng', feats: ['2 máy', 'Đổi thưởng'] },
  { key: 'pro', name: 'Pro', price: '149k/tháng', feats: ['4 máy', 'Thách đấu · Báo cáo'] },
];

export default function Settings() {
  const { account } = useAuth();
  const isAdmin = account?.role === 'admin';
  return (
    <Box pt={{ base: '130px', md: '80px', xl: '80px' }}>
      {!isAdmin && <SubscriptionCard />}
      <SimpleGrid columns={{ base: 1, lg: isAdmin ? 2 : 1 }} gap="20px" alignItems="start">
        <SystemCard />
        {isAdmin && <AdminsCard />}
      </SimpleGrid>
    </Box>
  );
}

function SubscriptionCard() {
  const { data, loading } = useApi(() => api.get('/billing/me/detail'));
  const headBg = useColorModeValue('#FBFAFF', '#1E1840');
  const line = useColorModeValue('#D8D5EC', '#2A2350');
  if (loading || !data) return null;
  const sub = data.subscription;
  const days = sub?.expiresAt ? Math.max(0, Math.ceil((new Date(sub.expiresAt) - Date.now()) / 86400000)) : null;
  const recs = [...(data.subRecords || [])].sort((a, b) => new Date(b.endAt) - new Date(a.endAt));
  return (
    <>
      {/* 💳 Gói & Subscription */}
      <Card mb="20px">
        <Flex align="center" mb="16px" wrap="wrap" gap="8px">
          <Text fontWeight="850" fontSize="16px">💳 Gói &amp; Subscription</Text>
          <Text ml="auto" fontSize="13px" color="secondaryGray.600">
            {sub?.status === 'active'
              ? <>Đang hoạt động{days != null ? <> · còn <b>{days} ngày</b> · gia hạn {new Date(sub.expiresAt).toLocaleDateString('vi-VN')}</> : ''}</>
              : 'Chưa có gói trả phí'}
          </Text>
        </Flex>
        <SimpleGrid columns={{ base: 1, md: 3 }} gap="14px">
          {TIERS.map((t) => {
            const cur = data.tier === t.key;
            return (
              <Box key={t.key} position="relative" borderRadius="18px" p="18px" border="1.5px solid"
                borderColor={cur ? 'brand.500' : line} boxShadow={cur ? '0 0 0 3px rgba(124,92,252,.15)' : 'none'}>
                {cur && <Badge position="absolute" top="-11px" left="50%" transform="translateX(-50%)" colorScheme="purple" borderRadius="99px" px="12px">✓ Đang dùng</Badge>}
                <Text fontWeight="850" fontSize="16px">{t.name}</Text>
                <Text fontWeight="850" fontSize="20px" my="6px">{t.price}</Text>
                {t.feats.map((f) => <Text key={f} fontSize="13px" color="secondaryGray.600">✓ {f}</Text>)}
              </Box>
            );
          })}
        </SimpleGrid>
        <Text fontSize="12.5px" color="secondaryGray.600" mt="12px">Nâng cấp / gia hạn gói thực hiện trong ứng dụng trên máy con.</Text>
      </Card>

      {/* 🧾 Lịch sử gói */}
      <Card mb="20px" p="0" overflow="hidden">
        <Text fontWeight="850" fontSize="16px" p="16px 18px 10px">🧾 Lịch sử gói ({recs.length})</Text>
        {recs.length === 0
          ? <Text px="18px" pb="16px" fontSize="sm" color="secondaryGray.600">Chưa có lịch sử thanh toán.</Text>
          : (
            <Box overflowX="auto">
              <Box as="table" w="100%" fontSize="13.5px" style={{ borderCollapse: 'collapse' }}>
                <Box as="thead"><Box as="tr">
                  {['Gói', 'Trạng thái', 'Giao dịch lúc', 'Ngày bắt đầu', 'Ngày kết thúc', 'Thanh toán'].map((h) => (
                    <Box as="th" key={h} textAlign={h === 'Thanh toán' ? 'right' : 'left'} p="10px 18px" fontSize="11.5px"
                      textTransform="uppercase" letterSpacing=".05em" color="secondaryGray.600" fontWeight="800" bg={headBg} whiteSpace="nowrap">{h}</Box>
                  ))}
                </Box></Box>
                <Box as="tbody">
                  {recs.map((r) => (
                    <Box as="tr" key={r.id}>
                      <Box as="td" p="11px 18px" borderTop="1px solid" borderColor="line"><RecordTier tier={r.tier} source={r.source} /></Box>
                      <Box as="td" p="11px 18px" borderTop="1px solid" borderColor="line"><RecordStatus startAt={r.startAt} endAt={r.endAt} /></Box>
                      <Box as="td" p="11px 18px" borderTop="1px solid" borderColor="line" whiteSpace="nowrap" color="secondaryGray.600">{fmtDateTime(r.createdAt)}</Box>
                      <Box as="td" p="11px 18px" borderTop="1px solid" borderColor="line">{new Date(r.startAt).toLocaleDateString('vi-VN')}</Box>
                      <Box as="td" p="11px 18px" borderTop="1px solid" borderColor="line" fontWeight="700">{new Date(r.endAt).toLocaleDateString('vi-VN')}</Box>
                      <Box as="td" p="11px 18px" borderTop="1px solid" borderColor="line" textAlign="right" fontWeight="800">{Number(r.amount).toLocaleString('vi-VN')} đ</Box>
                    </Box>
                  ))}
                </Box>
              </Box>
            </Box>
          )}
      </Card>
    </>
  );
}

function SRow({ icon, title, desc, children, last }) {
  const bg = useColorModeValue('#FBFAFF', '#1E1840');
  return (
    <Flex align="center" gap="13px" py="14px" borderBottom={last ? 'none' : '1px solid'} borderColor="line">
      <Flex w="42px" h="42px" borderRadius="12px" bg={bg} align="center" justify="center" fontSize="20px" flex="0 0 auto">{icon}</Flex>
      <Box flex="1"><Text fontWeight="800" fontSize="14px">{title}</Text>
        <Text fontSize="12.5px" color="secondaryGray.600">{desc}</Text></Box>
      {children}
    </Flex>
  );
}

function SystemCard() {
  const { colorMode, setColorMode } = useColorMode();
  const toast = useToast();
  const [notify, setNotify] = useState(() => localStorage.getItem('vdh_notify') !== '0');
  function toggleNotify() {
    setNotify((v) => {
      const nv = !v;
      localStorage.setItem('vdh_notify', nv ? '1' : '0');
      toast({ title: nv ? 'Đã bật thông báo' : 'Đã tắt thông báo', status: 'success', duration: 1500 });
      return nv;
    });
  }
  return (
    <Card>
      <Text fontWeight="850" fontSize="17px" mb="6px">⚙️ Cài đặt hệ thống</Text>
      <SRow icon="🌗" title="Giao diện" desc="Sáng / Tối (tự lưu lại)">
        <Select w="160px" size="sm" value={colorMode} onChange={(e) => setColorMode(e.target.value)}>
          <option value="light">Sáng</option><option value="dark">Tối</option>
        </Select>
      </SRow>
      <SRow icon="🌐" title="Ngôn ngữ" desc="Hiện chỉ hỗ trợ tiếng Việt">
        <Select w="160px" size="sm" defaultValue="vi" isDisabled><option value="vi">Tiếng Việt</option></Select>
      </SRow>
      <SRow icon="🔔" title="Thông báo" desc="Nhắc yêu cầu đổi thưởng, máy offline">
        <Switch isChecked={notify} onChange={toggleNotify} colorScheme="brand" />
      </SRow>
      <SRow icon="ℹ️" title="Phiên bản" desc="Vui Để Học Portal v2.0" last />
    </Card>
  );
}

function AdminsCard() {
  const toast = useToast();
  const [list, setList] = useState([]);
  const [username, setUsername] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const reload = () => api.get('/admin/admins').then(setList).catch(() => {});
  useEffect(() => { reload(); }, []);

  async function add(e) {
    e.preventDefault();
    if (username.trim().length < 3) return toast({ title: 'Tên đăng nhập tối thiểu 3 ký tự', status: 'warning' });
    if (password.length < 6) return toast({ title: 'Mật khẩu tối thiểu 6 ký tự', status: 'warning' });
    setBusy(true);
    try {
      await api.post('/admin/admins', { username: username.trim(), password, displayName: displayName.trim() || undefined });
      toast({ title: 'Đã thêm quản trị viên', status: 'success' });
      setUsername(''); setDisplayName(''); setPassword(''); reload();
    } catch (err) { toast({ title: err.message || 'Không thêm được', status: 'error' }); }
    finally { setBusy(false); }
  }

  return (
    <Card>
      <Text fontWeight="850" fontSize="17px" mb="10px">👑 Quản trị viên ({list.length})</Text>
      {list.map((a) => (
        <Flex key={a.id} align="center" py="10px" borderBottom="1px solid" borderColor="line">
          <Box flex="1"><Text fontWeight="700">{a.displayName || a.username}</Text>
            <Text fontSize="sm" color="secondaryGray.600">@{a.username}{a.email ? ` · ${a.email}` : ''}</Text></Box>
          <Badge colorScheme="purple">ADMIN</Badge>
        </Flex>
      ))}
      <Divider my="16px" />
      <Text fontWeight="700" mb="10px">Thêm quản trị viên</Text>
      <form onSubmit={add}>
        <SimpleGrid columns={{ base: 1, md: 3 }} gap="12px" mb="12px">
          <Input placeholder="Tên đăng nhập" value={username} onChange={(e) => setUsername(e.target.value)} />
          <Input placeholder="Tên hiển thị" value={displayName} onChange={(e) => setDisplayName(e.target.value)} />
          <Input type="password" placeholder="Mật khẩu" value={password} onChange={(e) => setPassword(e.target.value)} />
        </SimpleGrid>
        <Button type="submit" colorScheme="brand" isLoading={busy}>Thêm admin</Button>
      </form>
    </Card>
  );
}
